// ============================================================
// GitHubPRs.jsx
//
// A branded, responsive course content page for the
// "Pull Requests & Collaboration" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Pull Request lifecycle, Code Review etiquette,
//           and Forking workflows for Open Source.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import GitHubIcon             from "@mui/icons-material/GitHub";
import MergeTypeIcon          from "@mui/icons-material/MergeType";
import RateReviewIcon         from "@mui/icons-material/RateReview";
import AccountTreeIcon        from "@mui/icons-material/AccountTree";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import AltRouteIcon           from "@mui/icons-material/AltRoute";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 1.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitHubPRs() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<GitHubIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Team Collaboration"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Pull Requests & <span>Collaboration</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Pull Requests (PRs) are the heartbeat of modern software engineering. They facilitate code reviews, protect the main branch, and ensure that every change is discussed and vetted before reaching production.
                    </Typography>
                </Box>

                {/* 1. BRANCHING & PUSHING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AccountTreeIcon />} label="1. The Feature Branch Workflow" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Always keep your <code>main</code> branch clean. Create a dedicated branch for every feature or bug fix to allow for isolated development and review.
                    </Typography>
                    <CodeBlock
                        title="Local Branching Logic"
                        code={`# Create and switch to a new branch\ngit checkout -b feature/login-page\n\n# Commit your changes\ngit commit -am "feat: design login UI"\n\n# Push your branch to GitHub\ngit push origin feature/login-page`}
                    />
                </Box>

                {/* 2. OPENING A PR */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<MergeTypeIcon />} label="2. Proposing Changes" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Once pushed, navigate to GitHub to open a Pull Request. This initiates the discussion phase where reviewers can inspect your code.
                    </Typography>
                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                        <Grid container spacing={2}>
                            {["Navigate to 'Pull Requests' tab on GitHub", "Click 'Compare & pull request'", "Draft a clear title and descriptive body", "Assign reviewers and labels"].map((step, i) => (
                                <Grid item xs={12} sm={6} key={i}>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <Typography sx={{ color: BRAND.gold, fontWeight: 900 }}>{i + 1}.</Typography>
                                        <Typography sx={{ color: BRAND.white, fontSize: 13 }}>{step}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Box>

                {/* 3. CODE REVIEW */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RateReviewIcon />} label="3. The Code Review Cycle" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Collaboration is a two-way street. Reviewers provide feedback, and authors push updates to the same branch to address concerns.
                    </Typography>
                    <Grid container spacing={1.5}>
                        {[
                            { title: "Line Comments", desc: "Discussion occurs directly on specific lines of code for high precision." },
                            { title: "Approval Status", desc: "PRs can be approved, commented on, or changes can be requested." },
                            { title: "Status Checks", desc: "Automated tests (CI) must pass before a merge is permitted." },
                            { title: "Final Merge", desc: "Once approved and passing tests, the code is squashed and merged into main." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, borderBottom: `1px solid ${BRAND.surface}` }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 18 }} />
                                    <Typography sx={{ color: BRAND.white, fontSize: 13 }}>
                                        <strong style={{ color: BRAND.goldBright }}>{item.title}:</strong> {item.desc}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 4. FORKING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AltRouteIcon />} label="4. Forking for Open Source" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        If you don't have write access to a repository, use the <strong>Forking</strong> workflow. This creates your own copy of the repo to hack on.
                    </Typography>
                    <CodeBlock
                        title="Forking Workflow Commands"
                        code={`# Clone YOUR fork (not the original)\ngit clone https://github.com/your-username/original-repo.git\n\n# Push to your fork\ngit push origin feature-branch\n\n# Open PR from your fork back to the original repo`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Collaboration Etiquette" />
                    <Grid container spacing={1.5}>
                        {[
                            "Keep Pull Requests small; easier to review and faster to merge.",
                            "Write a descriptive PR body explaining 'Why' changes were made.",
                            "Be respectful in code reviews; focus on the code, not the person.",
                            "Reviewers: Provide constructive suggestions, not just criticisms.",
                            "Ensure all CI/CD status checks are green before requesting a review.",
                            "Delete your feature branch after the merge to keep the repo tidy."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Use <strong>Draft Pull Requests</strong> if you want to share your progress and get early feedback without signaling that the code is ready for a final merge!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
